import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { mergeProps as _mergeProps, mergeExternalProps as _mergeExternalProps } from "@terraindigital/static-styles-react";
import { GatsbyImage, getImage, IGatsbyImageData, ImageDataLike } from 'gatsby-plugin-image';
import React from 'react';
import { CsAssetSize } from '../../../sourcing/data/contentstack/content-item';
import { buildResponsiveContentstackSources, getSizesProp } from '../../../sourcing/data/contentstack/asset-helpers';
import { ENVIRONMENT } from '../../../constants';

/**
 * Re-export CsAssetSize for convenience when using Image component
 * Use getSizesProp(CsAssetSize.XXX) to get the sizes string:
 *
 * Viewport-based (for full-bleed layouts outside containers):
 * - FULL_100VW: '100vw' - Full viewport width
 * - HALF_100VW: '(max-width: 767px) 100vw, 50vw' - Half viewport (2 columns)
 * - THIRD_100VW: '(max-width: 767px) 100vw, 33.33vw' - Third viewport (3 columns)
 * - QTR_100VW: '(max-width: 767px) 100vw, 25vw' - Quarter viewport (4 columns)
 *
 * Container-constrained (for content inside 1440px max-width container):
 * - FULL_MAX_WIDTH: '(max-width: 1440px) 100vw, 1440px' - Full container width
 * - HALF_MAX_WIDTH: '(max-width: 767px) 100vw, (max-width: 1440px) 50vw, 720px' - Half container
 * - THIRD_MAX_WIDTH: '(max-width: 767px) 100vw, (max-width: 1440px) 33.33vw, 480px' - Third container
 * - QTR_MAX_WIDTH: '(max-width: 767px) 100vw, (max-width: 1440px) 25vw, 360px' - Quarter container
 */
export { CsAssetSize, getSizesProp };
interface ImageProps {
  image?: Partial<ContentStackImage> | null;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'low' | 'auto';
  sizes?: string;
  [key: string]: unknown;
}
const Image = ({
  image,
  loading: loadingProp,
  alt,
  fetchPriority,
  sizes: sizesProp,
  style: styleProp,
  ...rest
}: ImageProps): JSX.Element => {
  const loading = loadingProp ?? 'lazy';
  const {
    filename,
    contentStackAsset,
    description,
    url
  } = image || {};
  const altText = alt || description || '';
  const imagePath = filename || url;
  if (imagePath && url && (imagePath.includes('.svg') || imagePath.includes('.gif'))) {
    // Transform URL to use edge function if in Netlify environment
    // Only transform if it's a full ContentStack URL
    let transformedUrl = url;
    if (ENVIRONMENT.IS_RUNNING_ON_NETLIFY && url.startsWith('https://images.contentstack.io/')) {
      try {
        transformedUrl = `/dcs-images${new URL(url).pathname}`;
      } catch {
        transformedUrl = url;
      }
    }
    const imgProps = {
      ...rest,
      ...(styleProp && {
        style: styleProp
      }),
      ...(fetchPriority && {
        fetchpriority: fetchPriority
      })
    };
    return <img {..._mergeProps({
      loading: loading,
      decoding: "async",
      src: transformedUrl,
      alt: altText
    }, imgProps as React.ImgHTMLAttributes<HTMLImageElement>)} />;
  }
  const isCriticalImage = loading === 'eager' && fetchPriority === 'high';
  if (contentStackAsset && typeof contentStackAsset === 'object' && 'childImageSharp' in contentStackAsset && getImage(contentStackAsset?.childImageSharp as ImageDataLike)) {
    const img = getImage(contentStackAsset?.childImageSharp as ImageDataLike) as IGatsbyImageData;

    // Use provided sizes or intelligent default based on priority
    const sizes = sizesProp || (isCriticalImage ? getSizesProp(CsAssetSize.FULL_100VW) : getSizesProp(CsAssetSize.FULL_MAX_WIDTH));
    return <GatsbyImage {..._mergeExternalProps({
      image: img,
      alt: altText,
      loading: loading
    }, rest, styleProp && {
      style: styleProp
    }, fetchPriority && {
      fetchpriority: fetchPriority
    }, sizes && {
      sizes
    })} />;
  }
  if (image && getImage(image)) {
    const img = getImage(image as ImageDataLike) as IGatsbyImageData;

    // Use provided sizes or intelligent default based on priority
    const sizes = sizesProp || (isCriticalImage ? getSizesProp(CsAssetSize.FULL_100VW) : getSizesProp(CsAssetSize.FULL_MAX_WIDTH));
    return <GatsbyImage {..._mergeExternalProps({
      image: img,
      alt: altText,
      loading: loading
    }, rest, styleProp && {
      style: styleProp
    }, fetchPriority && {
      fetchpriority: fetchPriority
    }, sizes && {
      sizes
    })} />;
  }
  if (imagePath && url) {
    // Fallback for images without contentStackAsset data — sized Contentstack transforms
    const {
      src: fallbackSrc,
      srcSet
    } = buildResponsiveContentstackSources(url);
    const imgProps = {
      ...rest,
      ...(styleProp && {
        style: styleProp
      }),
      ...(fetchPriority && {
        fetchpriority: fetchPriority
      })
    };

    // Smart sizes default: Use provided sizes or intelligent default based on priority
    // Hero/critical images (eager + high priority) are typically full-width
    // Content images are typically constrained by the 1440px container
    const sizes = sizesProp || (isCriticalImage ? getSizesProp(CsAssetSize.FULL_100VW) : getSizesProp(CsAssetSize.FULL_MAX_WIDTH));
    return <img {..._mergeProps({
      loading: loading,
      decoding: "async",
      src: fallbackSrc
    }, srcSet && {
      srcSet
    }, srcSet && {
      sizes
    }, {
      alt: altText
    }, imgProps as React.ImgHTMLAttributes<HTMLImageElement>)} />;
  }

  // Should be able to function as a 'box' layout wise even with a missing image
  return <div {..._mergeProps(rest, styleProp && {
    style: styleProp
  })} />;
};
export default Image;